import {
  CSSProperties,
  ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Base, BorderIcon, Container, Label, List, ListContainer, ListItem,
} from './styles';
import { useOuterClickNotifier } from '~/hooks';
import * as IconNames from 'icons';
import { colors } from '~/styles/colors';

type IconName = keyof typeof IconNames;
export type Option = {
  label: string;
  value: string;
  iconName?: IconName;
  comingSoon?: boolean
};

interface BaseSelectProps {
  icon: IconName;
  options: Option[];
  placeholder: string;
  disabled?: boolean;
  style?: CSSProperties;
  onSelect: (value: string) => void;
}

interface SingleSelectProps extends BaseSelectProps {
  multiple?: false;
  value?: string;
}

interface MultiSelectProps extends BaseSelectProps {
  multiple: true;
  value?: string[];
}

type SelectProps = SingleSelectProps | MultiSelectProps;

export function Select({
  disabled,
  icon,
  options,
  placeholder,
  value,
  onSelect,
  style,
  multiple,
}: Readonly<SelectProps>): ReactElement {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const clickOutsideCallback = useCallback(() => setIsOpen(false), []);
  useOuterClickNotifier(clickOutsideCallback, containerRef);

  function toggleOptions(): void {
    if (disabled) return;
    setIsOpen(!isOpen);
  }

  function onClickItem(itemValue: string, comingSoon?: boolean): void {
    if (comingSoon) return;
    setIsOpen(false);
    onSelect(itemValue);
  }

  function renderIcon(icon: string, border?: true): ReactElement | null {
    const Icon = IconNames[icon];
    const iconElement = (
      <Icon
        color={border ? colors.icon['strong-900'] : colors.icon['primary-base']}
        size="14"
      />
    );

    if (border) {
      return <BorderIcon>{iconElement}</BorderIcon>;
    }

    return iconElement;
  }

  function renderLabel(): string {
    if (!value) {
      return placeholder;
    }

    if (multiple) {
      const selectedOptions = options.filter((item) => value.includes(item.value));

      if (selectedOptions.length === 0) {
        return placeholder;
      }

      if (selectedOptions.length > 1) {
        return `${selectedOptions.length} selecionados`;
      }

      return selectedOptions[0].label;
    }

    return options.find((item) => item.value === value)?.label ?? placeholder;
  }

  function isSelectedOption(optionValue: string): boolean {
    if (multiple) {
      return (value ?? []).includes(optionValue);
    }

    return value === optionValue;
  }

  const definedValue = useMemo(
    () => (multiple ? !!value && value.length > 0 : !!value),
    [multiple, value],
  );

  return (
    <Container ref={containerRef} style={style}>
      <Base selected={definedValue} onClick={toggleOptions} open={isOpen}>
        {renderIcon(icon)}
        <Label selected={definedValue}>{renderLabel()}</Label>
        <IconNames.ChevronRight
          rotate={isOpen ? '270deg' : '90deg'}
          color={
            definedValue ? colors.icon['strong-900'] : colors.icon['soft-400']
          }
          size="14"
        />
      </Base>
      {isOpen && (
        <ListContainer>
          <List>
            {options.map((option) => {
              const isSelected = isSelectedOption(option.value);

              return (
                <ListItem
                  key={`option-${option.value.trim().toLowerCase()}`}
                  selected={isSelected}
                  comingSoon={option.comingSoon}
                  onClick={() => onClickItem(option.value, option.comingSoon)}
                >
                  {option.iconName && renderIcon(option.iconName, true)}
                  <span>{option.label}</span>
                </ListItem>
              );
            })}
          </List>
        </ListContainer>
      )}
      <div />
    </Container>
  );
}
